/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import isCableURL from "../../utils/isCableURL";

import axios from "connection";

import ConfigSlice, { ConfigSliceFromRoot } from "types/ConfigSlice";

import MonetaryFormat from "types/MonetaryFormat";
import { getDeviceSize, getDeviceType } from "utils/deviceDetect";
import getOpco from "utils/getOpco";
import { getSource } from "utils/getSource";
import LoginUrl from "types/LoginUrl";
import { clearOffersState } from "state/offers-slice";
import { clearCartState } from "state/cart-slice";

import { API_URL, PPE_PATH } from "config";
import Accessibility from "types/Accessibility";

const initialState: ConfigSlice = {
  loaded: false,
  openidUrls: [],
  modal: {
    showModal: false,
    errorModal: false,
    cookieModal: false,
    title: "",
    description: "",
    cross: false,
    children: null,
    textAlign: null,
    icon: null,
    type: "",
    size: "small",
    customButtons: null,
  },
  loader: {
    showLoader: true,
    messageHeader: "",
    messageText: "",
  },
  IframeLayer: {
    showIframeLayer: false,
    url: null,
  },
  toast: {
    show: false,
    icon: null,
    children: null,
    type: "",
    id: "",
    ids: [],
  },
  isStickyAreaVisible: false,
  isMenuOpen: false,
  cdnUrl: "",
};

export const fetchConfigFromAPI = createAsyncThunk("config/fetchConfig", async () => {
  const source = getSource();
  const queryParams = source ? `&s=${source}` : "";

  try {
    const result = await axios.get(`${API_URL}${PPE_PATH}/config?opco=${getOpco()}${queryParams}`);

    return result.data;
  } catch (error) {
    return Promise.reject(error);
  }
});

export const sendULFEvent = createAsyncThunk("config/sendULFEvent", async (data: any) => {
  const body = {
    channel: getDeviceSize(),
    design: "visual",
    "offer-name": null,
    partner: null,
    page: window.location.pathname,
    source: "subscriptionplus",
    "x-vf-trace-timestamp": Date.now(),
    reason: null,
    "device-type": getDeviceType(),
    proposition: "Subscription_Plus",
  };

  const initialData = {
    "x-vf-trace-timestamp": Date.now(),
    "jsession-id": data.jsessionId,
    "usecase-id": data.uid,
    "cookie-accepted": data.isTrackingAllowed,
  };

  const postData = data.isTrackingAllowed === "true" ? Object.assign({}, body, data, initialData) : initialData;

  try {
    const result = await axios.post(`${API_URL}${PPE_PATH}/ulf`, postData);

    return result;
  } catch (error) {
    return Promise.reject(error);
  }
});

export const redirectToID = createAsyncThunk("redirect/vodafoneID", async (_: any, { getState, dispatch }: any) => {
  const url = getState().config.openidUrls?.find((el: LoginUrl) =>
    isCableURL() ? el.type === "silver" : el.type === "gold" ? el.type === "gold" : el.type === "bronze",
  )?.url as string;

  if (url) {
    window.location.assign(url);
    dispatch(clearCartState());
    dispatch(clearOffersState());
    dispatch(clearConfigState());
  }
});

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setConfig: (state, action) => {
      return {
        ...state,
        loaded: true,
        openidUrls: { ...action.payload.openidUrls },
      };
    },
    setModal: (state, action) => {
      return {
        ...state,
        modal: {
          ...initialState.modal,
          ...action.payload,
        },
      };
    },
    clearConfigState: () => {
      return {
        ...initialState,
      };
    },
    setLoader: (state, action) => {
      return {
        ...state,
        loader: {
          ...state.loader,
          ...action.payload,
        },
      };
    },
    setIframeLayer: (state, action) => {
      return {
        ...state,
        IframeLayer: {
          ...state.IframeLayer,
          ...action.payload,
        },
      };
    },
    setToast: (state, action) => {
      return {
        ...state,
        toast: {
          ...state.toast,
          ...action.payload,
        },
      };
    },
    setIsStickyAreaVisible: (state, action) => {
      return {
        ...state,
        isStickyAreaVisible: action.payload,
      };
    },
    setIsMenuOpen: (state, action) => {
      return {
        ...state,
        isMenuOpen: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfigFromAPI.fulfilled, (state, action) => {
        state.openidUrls = [...action.payload.openidUrls];
        state.monetaryFormat = { ...action.payload.monetaryFormat };
        state.accessibility = { ...action.payload.accessibility };
        state.cdnUrl = action.payload.assets || "";
        state.loaded = true;
      })
      .addCase(fetchConfigFromAPI.pending, (state) => {
        state.loaded = false;
      })
      .addCase(fetchConfigFromAPI.rejected, (state) => {
        state.loader.showLoader = false;
        state.modal.showModal = true;
        state.modal.errorModal = true;
      });
  },
});

export const {
  setConfig,
  setModal,
  clearConfigState,
  setLoader,
  setIframeLayer,
  setToast,
  setIsStickyAreaVisible,
  setIsMenuOpen,
} = configSlice.actions;

export const configState = (state: ConfigSliceFromRoot): ConfigSlice => state.config;

export const monetaryConfig = (state: ConfigSliceFromRoot): MonetaryFormat | undefined => state.config.monetaryFormat;

export const accessibilityConfig = (state: ConfigSliceFromRoot): Accessibility | undefined =>
  state.config.accessibility;

export default configSlice.reducer;
